/**
 * Maps a `seller-service-price` JSON:API listing into the display catalogue tree (ticket #5598).
 * Pure and framework-free, so it is unit-tested in isolation.
 *
 * Each row prices a service and names its position through three relationships, each carrying a
 * stable resource id and resolved to a display title against the document's `included` resources:
 *   - `category` always → the category the row belongs to.
 *   - `sub1Service` → the sub1 service, or empty `data` when the row prices the whole category.
 *   - `sub2Detail` → the sub2 detail, or empty `data` when the form asks no detail.
 *
 * So a row lands at the deepest level its relationships describe:
 *   - category only (no `sub1Service`) → prices the whole category (rendered as a single row
 *     labeled with the category title, since the UI has no category-level price slot).
 *   - category and service → the sub1 row carries the price.
 *   - category + service + detail → the sub2 row carries the price, nested under its sub1.
 *
 * Grouping is keyed by the relationship **id** (so services with the same title stay distinct),
 * with the resolved title used only as the display label. The catalogue ids exposed to the UI are
 * synthesized from those real relationship ids (DOM-safe) purely for React keys / `Collapsible`
 * ids — never ordinal counters, so ids stay stable across reorders and refetches:
 *   - category → `cat-{id}`.
 *   - sub1 service → `svc-{id}` for a standard `choice-value`, `custom-svc-{id}` for a
 *     `custom-service` (the type prefix keeps ids from colliding, since ids are only unique
 *     within a resource type).
 *   - sub2 detail → `dtl-{id}` for a standard `choice-value`, `custom-dtl-{id}` for a
 *     `custom-detail`.
 * Row insertion order is preserved throughout.
 */

import {
  CatalogueCategory,
  CatalogueNodeTracking,
  CatalogueService,
  JobFormQuestionAnswer,
  SellerServicePriceAttributes,
  SellerServicePriceDocument,
  ServiceBookingTarget,
  ServiceInfo,
  ServicePrice,
  ServicePriceIncludedResource,
  ServicePriceRelationship
} from './types';

const toNumber = (value?: string | null): number | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const parsed = Number(value);

  return Number.isNaN(parsed) ? undefined : parsed;
};

const mapPrice = (attrs: SellerServicePriceAttributes): ServicePrice => ({
  priceType: attrs.priceType,
  priceUnit: attrs.priceUnit,
  // NaN when unparseable — the formatter guards and renders the row label-only.
  amount: toNumber(attrs.amount) ?? Number.NaN,
  amountMax: toNumber(attrs.amountMax)
});

// Blank copy must read as absent, not as an empty section: `null` hides the info affordance
const toInfoText = (value?: string | null): string | undefined =>
  value !== undefined && value !== null && value.trim() !== '' ? value : undefined;

const mapServiceInfo = (attrs: SellerServicePriceAttributes): ServiceInfo | undefined => {
  const includedExcluded = toInfoText(attrs.includedExcluded);
  const termsAndConditions = toInfoText(attrs.termsAndConditions);

  if (includedExcluded === undefined && termsAndConditions === undefined) {
    return undefined;
  }

  return { includedExcluded, termsAndConditions };
};

/** Indexes sideloaded label resources by `type:id` for O(1) relationship resolution. */
const indexIncluded = (included: Array<ServicePriceIncludedResource> = []): Map<string, ServicePriceIncludedResource> => {
  const resourceByRef = new Map<string, ServicePriceIncludedResource>();
  for (const resource of included) {
    resourceByRef.set(`${resource.type}:${resource.id}`, resource);
  }

  return resourceByRef;
};

/** Coerces a form id (sent as a number or a numeric string) to a number; `undefined` when unusable. */
const toFormId = (value?: number | string | null): number | undefined => {
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value : undefined;
  }
  if (typeof value !== 'string' || !/^\d+$/.test(value)) {
    return undefined;
  }

  return Number(value);
};

/**
 * The job-form question/answer pair a sub1/sub2 record maps to, or `undefined` when the record
 * carries no (or a malformed) pair — the click then degrades to a coarser skip.
 *
 * There is no `answerId` attribute in the payload: a `choice-value` record **is** the answer, so
 * its own resource `id` is the chosen option id. Only `questionId` names the question it answers,
 * and a record without one (a `custom-service`/`custom-detail` the pro typed in) yields no pair.
 */
const toQuestionAnswer = (resource?: ServicePriceIncludedResource): JobFormQuestionAnswer | undefined => {
  const questionId = toFormId(resource?.attributes.questionId);
  const answerId = toFormId(resource?.id);

  return questionId !== undefined && answerId !== undefined ? { questionId, answerId } : undefined;
};

/**
 * A relationship resolved to its stable resource `type`/`id`, its sideloaded display `title`
 * and (sub1/sub2 only) the job-form pair the record maps to — whose answer id is that same
 * resource `id`, so the pair only resolves for a record that was actually sideloaded.
 */
interface ResolvedRef {
  type: string;
  id: string;
  title: string;
  pair?: JobFormQuestionAnswer;
  // Only the sideloaded `categories` record carries this; sub1/sub2 info rides on the price row
  info?: string;
}

/** Resolves a relationship to its `{ type, id, title, pair }`, or `undefined` when the link is empty. */
const resolveRef = (
  resourceByRef: Map<string, ServicePriceIncludedResource>,
  relationship?: ServicePriceRelationship
): ResolvedRef | undefined => {
  const linkage = relationship?.data;
  if (!linkage) {
    return undefined;
  }
  const resource = resourceByRef.get(`${linkage.type}:${linkage.id}`);

  return {
    type: linkage.type,
    id: linkage.id,
    title: resource?.attributes.title ?? '',
    pair: toQuestionAnswer(resource),
    info: toInfoText(resource?.attributes.servicePriceInfo)
  };
};

/**
 * The booking target for a row: the real category id plus the ancestor chain's pre-answers.
 *
 * The chain is truncated at the first level whose ids are missing. A deeper answer
 * cannot be applied without the shallower one it is conditioned on — so a detail without a
 * service pair degrades to the service-level skip, and a service without ids to category-only.
 */
const buildBookingTarget = (category: ResolvedRef, sub1?: ResolvedRef, sub2?: ResolvedRef): ServiceBookingTarget => {
  const pairs: Array<JobFormQuestionAnswer> = [];

  if (sub1?.pair) {
    pairs.push(sub1.pair);

    if (sub2?.pair) {
      pairs.push(sub2.pair);
    }
  }

  return { categoryId: category.id, pairs };
};

/**
 * The raw backend ids/titles for a node, as `Services Section Clicked` sends them.
 *
 * `isFormMapped` reads the deepest supplied ref's *own* pair rather than the booking chain's,
 * which {@link buildBookingTarget} truncates at the first missing level — a detail that does
 * carry form ids under a service that does not would otherwise report `false`.
 */
const buildTracking = (category: ResolvedRef, sub1?: ResolvedRef, sub2?: ResolvedRef): CatalogueNodeTracking => ({
  categoryId: category.id,
  categoryTitle: category.title,
  serviceId: sub1?.id,
  serviceTitle: sub1?.title,
  detailId: sub2?.id,
  detailTitle: sub2?.title,
  isFormMapped: (sub2 ?? sub1)?.pair !== undefined
});

/** Resource-type → catalogue-id prefix for a sub1 service row; `custom-service` gets a distinct prefix. */
const SUB1_ID_PREFIX: Record<string, string> = {
  'choice-value': 'svc',
  'custom-service': 'custom-svc'
};

/** Resource-type → catalogue-id prefix for a sub2 detail row; `custom-detail` gets a distinct prefix. */
const SUB2_ID_PREFIX: Record<string, string> = {
  'choice-value': 'dtl',
  'custom-detail': 'custom-dtl'
};

/** DOM-safe catalogue id for a category, keyed by its real relationship id. */
const categoryCatalogueId = (category: ResolvedRef): string => `cat-${category.id}`;

/** DOM-safe catalogue id for a sub1 service — type-prefixed so cross-type ids never collide. */
const sub1CatalogueId = (ref: ResolvedRef): string => `${SUB1_ID_PREFIX[ref.type] ?? 'svc'}-${ref.id}`;

/** DOM-safe catalogue id for a sub2 detail — type-prefixed so cross-type ids never collide. */
const sub2CatalogueId = (ref: ResolvedRef): string => `${SUB2_ID_PREFIX[ref.type] ?? 'dtl'}-${ref.id}`;

/** An ordered category bucket: its synthesized React-key id, display label, and sub1 services. */
interface CategoryBucket {
  catalogueId: string;
  label: string;
  info?: string;
  tracking: CatalogueNodeTracking;
  /** sub1 services keyed by their synthesized catalogue id (or the whole-category id). */
  services: Map<string, CatalogueService>;
}

/**
 * Groups the price rows into the ordered `Category → sub1 → sub2` tree consumed by
 * {@link ServicesCatalogue}, keyed by relationship id. Row insertion order is preserved.
 */
export const mapServicePricesToCatalogue = (doc: SellerServicePriceDocument): { categories: Array<CatalogueCategory> } => {
  const resourceByRef = indexIncluded(doc.included);
  // Ordered category buckets keyed by their synthesized catalogue id, each holding its sub1
  // services keyed by their own synthesized catalogue id.
  const categoryOrder: Array<string> = [];
  const buckets: Record<string, CategoryBucket> = {};

  const ensureCategory = (category: ResolvedRef): CategoryBucket => {
    const catalogueId = categoryCatalogueId(category);
    if (!buckets[catalogueId]) {
      buckets[catalogueId] = {
        catalogueId,
        label: category.title,
        info: category.info,
        tracking: buildTracking(category),
        services: new Map()
      };
      categoryOrder.push(catalogueId);
    }

    return buckets[catalogueId];
  };

  const ensureSub1 = (
    bucket: CategoryBucket,
    serviceId: string,
    label: string,
    booking: ServiceBookingTarget,
    tracking: CatalogueNodeTracking
  ): CatalogueService => {
    const existing = bucket.services.get(serviceId);
    if (existing) {
      return existing;
    }
    const created: CatalogueService = {
      serviceId,
      categoryId: bucket.catalogueId,
      level: 'sub1',
      label,
      booking,
      tracking
    };
    bucket.services.set(serviceId, created);

    return created;
  };

  for (const row of doc.data) {
    const relationships = row.relationships;
    const category = resolveRef(resourceByRef, relationships?.category);
    if (!category) {
      // The category link is present by contract — skip a row that lacks it rather than crash.
      continue;
    }

    const bucket = ensureCategory(category);
    const price = mapPrice(row.attributes);
    const info = mapServiceInfo(row.attributes);
    const sub1Ref = resolveRef(resourceByRef, relationships?.sub1Service);
    const sub2Ref = resolveRef(resourceByRef, relationships?.sub2Detail);

    if (!sub1Ref) {
      // Whole-category price: synthesize a single sub1 row labeled with the category title. Its id
      // derives from the category id, so it stays stable and distinct from any real service id.
      const wholeCategory = ensureSub1(
        bucket,
        `${bucket.catalogueId}-whole`,
        category.title,
        buildBookingTarget(category),
        buildTracking(category)
      );
      wholeCategory.price = price;
      wholeCategory.info = info;
      continue;
    }

    const sub1 = ensureSub1(
      bucket,
      sub1CatalogueId(sub1Ref),
      sub1Ref.title,
      buildBookingTarget(category, sub1Ref),
      buildTracking(category, sub1Ref)
    );

    if (!sub2Ref) {
      // sub1-level price.
      sub1.price = price;
      sub1.info = info;
      continue;
    }

    // sub2-level price nested under its sub1.
    const child: CatalogueService = {
      serviceId: sub2CatalogueId(sub2Ref),
      categoryId: bucket.catalogueId,
      level: 'sub2',
      label: sub2Ref.title,
      price,
      info,
      booking: buildBookingTarget(category, sub1Ref, sub2Ref),
      tracking: buildTracking(category, sub1Ref, sub2Ref)
    };
    sub1.children = [...(sub1.children ?? []), child];
  }

  const categories: Array<CatalogueCategory> = categoryOrder.map(catalogueId => {
    const bucket = buckets[catalogueId];

    return {
      categoryId: bucket.catalogueId,
      label: bucket.label,
      info: bucket.info,
      services: Array.from(bucket.services.values()),
      tracking: bucket.tracking
    };
  });

  return { categories };
};
